import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Link,
  Select,
  Text,
} from '@chakra-ui/react';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import BuilderSwitch from '../BuilderSwitch';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import {
  removeQueryParam,
  setQueryParamProp,
  setQueryParamConnectField,
} from '../../../../store/redux/slices/form.slice';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import { QueryParamState } from '../../../../store/types/form';
import { getTemplateFieldDisplayNameFromId } from '../utils/fieldHelpers';
import FieldEditIndicator from '../FieldEditIndicator';
import { useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import { LabelWithTooltip } from '../LabelWithTooltip';

interface CustomQueryParameterProps extends QueryParamState {
  index: number;
  isDisabled?: boolean;
}

const CustomQueryParameter = ({
  _clientId,
  index,
  id,
  required,
  isDisabled = false,
}: CustomQueryParameterProps) => {
  const templateFields = useAppSelector((state) => state.template.fields);
  const queryParams = useAppSelector((state) => state.form.queryParams);
  const form = useAppSelector((state) => state.form);
  const errors = useAppSelector((state) => state.templateErrors);
  const connectedFieldError = errors[`form.queryParams.${index}.id`];
  const dispatch = useAppDispatch();
  const { handleFocus, handleBlur, validateSection } =
    useTemplateFieldValidation('form');
  const queryParamRef = useRef<HTMLDivElement>(null);
  const requiredFieldRef = useRef<HTMLDivElement>(null);
  const fieldRefMap = useMemo(
    () => ({
      [`queryParams.${index}.id`]: queryParamRef,
      [`queryParams.${index}.required`]: requiredFieldRef,
    }),
    [index]
  );
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap,
  });
  const templateFieldOptions = useMemo(() => {
    return (
      Object.values(templateFields).flatMap((section) =>
        Object.values(section ?? {})
      ) as Array<{ id?: string; label?: string } | null>
    )
      .filter((f): f is { id: string; label?: string } => f != null && !!f.id)
      .map((f) => ({ id: f.id, label: f.label ?? f.id }));
  }, [templateFields]);

  const selectedByOtherParams = new Set(
    queryParams
      .filter((q) => q._clientId !== _clientId && q.id)
      .map((q) => q.id)
  );

  return (
    <Box
      id={`query-param-${index}`}
      opacity={isDisabled ? 0.6 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      <FieldEditIndicator
        fieldRef={queryParamRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="63px"
      >
        <FormControl isInvalid={!!connectedFieldError} mt={2}>
          <LabelWithTooltip
            label="Select field to connect"
            tooltip="Choose which form field will receive data from your URL."
          />
          <Select
            value={id || ''}
            onFocus={() => handleFocus(`queryParams.${index}.id`)}
            onBlur={handleBlur}
            onChange={(e) => {
              const fieldId = e.target.value;
              const displayName =
                getTemplateFieldDisplayNameFromId(fieldId) ||
                templateFieldOptions.find((o) => o.id === fieldId)?.label ||
                fieldId;
              dispatch(
                setQueryParamConnectField({
                  queryParamClientId: _clientId,
                  templateFieldId: fieldId,
                  templateFieldLabel: displayName,
                })
              );
              const updatedQueryParams = queryParams.map((q) =>
                q._clientId === _clientId
                  ? { ...q, id: fieldId, name: displayName }
                  : q
              );
              validateSection({ ...form, queryParams: updatedQueryParams });
            }}
            placeholder="Select a field..."
            mt={4}
          >
            {templateFieldOptions.map((field) => (
              <option
                key={field.id}
                value={field.id}
                disabled={selectedByOtherParams.has(field.id)}
              >
                {field.label ?? field.id}
              </option>
            ))}
          </Select>
          {connectedFieldError && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {connectedFieldError}
            </FormErrorMessage>
          )}
        </FormControl>
        <FieldEditIndicator
          fieldRef={requiredFieldRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="12px"
        >
          <FormControl>
            <Flex
              direction="row"
              width="100%"
              justifyContent="space-between"
              mt={4}
            >
              <Text>Require parameter?</Text>
              <BuilderSwitch
                isChecked={required}
                onFocus={() => handleFocus(`queryParams.${index}.required`)}
                onBlur={handleBlur}
                onChange={(e) =>
                  dispatch(
                    setQueryParamProp({
                      queryParamClientId: _clientId,
                      key: 'required',
                      value: e.target.checked,
                    })
                  )
                }
              />
            </Flex>
          </FormControl>
        </FieldEditIndicator>
        <Flex mt={4} alignItems="center" justify="flex-end" gap={2}>
          <Link
            variant="orange"
            fontSize="14px"
            onClick={() => dispatch(removeQueryParam(_clientId))}
          >
            Remove parameter
          </Link>
        </Flex>
        <Divider borderColor="primaryBackground" my={4} />
      </FieldEditIndicator>
    </Box>
  );
};

export default CustomQueryParameter;
